/**
 * Autocomplete input that filters options and keeps a stable selected label.
 */

import * as React from 'react';
import { Input as ShadcnInput } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SelectOption } from './Select';

/** Skeleton bar widths while options load. */
const SKELETON_WIDTHS = ['70%', '55%', '80%'];

/** Scroll distance from bottom to trigger load-more. */
const SCROLL_LOAD_THRESHOLD_PX = 40;

/** One flex child so highlighted chunks wrap as a single label, not as columns. */
const HIGHLIGHT_WRAP_CLASS = 'min-w-0 flex-1 text-left';

/** Highlight contiguous `query` hits; wrap in a span because dropdown items are flex. */
function highlightMatch(text: string, query: string, minSearchLength: number): React.ReactNode {
  if (!query || query.length < minSearchLength) {
    return <span className={HIGHLIGHT_WRAP_CLASS}>{text}</span>;
  }

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));
  const seen = new Map<string, number>();
  return (
    <span className={HIGHLIGHT_WRAP_CLASS}>
      {parts.map((part) => {
        const count = (seen.get(part) ?? 0) + 1;
        seen.set(part, count);
        const key = `${part}-${count}`;
        if (part.toLowerCase() !== query.toLowerCase()) {
          return <React.Fragment key={key}>{part}</React.Fragment>;
        }
        return (
          <strong key={key} className="font-bold text-[var(--color-2-nuance-1)]">
            {part}
          </strong>
        );
      })}
    </span>
  );
}

/** Props for {@link Autocomplete}. */
interface AutocompleteProps {
  label?: string;
  placeholder?: string;
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onSearch?: (query: string) => void | Promise<void>;
  onLoadMore?: () => void;
  hasMore?: boolean;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  minSearchLength?: number;
  state?: 'default' | 'error' | 'success' | 'warning';
  inputSize?: 'sm' | 'md';
}

/** Labeled autocomplete field with async or local options. */
export const Autocomplete: React.FC<AutocompleteProps> = ({
  label,
  placeholder = 'Search...',
  options,
  value: _value,
  onChange,
  onBlur,
  onSearch: _onSearch,
  onLoadMore,
  hasMore,
  error,
  helperText,
  required,
  disabled,
  isLoading = false,
  className,
  minSearchLength = 2,
  state = 'default',
  inputSize = 'md',
}) => {
  const value = _value;
  const onSearch = _onSearch;
  const [isOpen, setIsOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const optionsListRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isEditingRef = React.useRef(false);
  const inputId = `autocomplete-${label?.toLowerCase().replaceAll(/\s+/g, '-')}`;

  const selectedOption = options.find((opt) => opt.value === value);

  const filteredOptions = React.useMemo(() => {
    if (!searchQuery || searchQuery.length < minSearchLength) return options;

    // Strip trailing "(dept)" syntax before client-side filtering so "Jules (50)"
    // still matches labels like "Jules Ferry (Nord - 50)" returned by the server.
    const trailingParen = searchQuery.lastIndexOf('(');
    const stripped =
      trailingParen >= 0 && searchQuery.trimEnd().endsWith(')')
        ? searchQuery.slice(0, trailingParen)
        : searchQuery;
    const query = stripped.trim().toLowerCase();
    if (!query) return options;

    return options.filter(
      (opt) =>
        (opt.label ?? '').toLowerCase().includes(query) ||
        (opt.value ?? '').toLowerCase().includes(query),
    );
  }, [options, searchQuery, minSearchLength]);

  /** Update the query, clear an outdated selection, and request options. */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    isEditingRef.current = true;
    setSearchQuery(query);
    setIsOpen(true);
    setActiveIndex(-1);

    // Clear selection while the user retypes so the label is not forced back
    if (value && query !== selectedOption?.label) {
      onChange?.('');
    }

    if (query.length >= minSearchLength) {
      void onSearch?.(query);
    }
  };

  /** Commit an option and show its label in the field. */
  const handleSelect = (optionValue: string) => {
    isEditingRef.current = false;
    onChange?.(optionValue);
    const selected = options.find((opt) => opt.value === optionValue);
    setSearchQuery(selected?.label || '');
    setIsOpen(false);
    setActiveIndex(-1);
  };

  /** Clear the value and refocus the field. */
  const handleClear = () => {
    isEditingRef.current = true;
    onChange?.('');
    setSearchQuery('');
    setIsOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  /** Arrow-navigate the list; Enter selects; Escape closes. */
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || filteredOptions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
          handleSelect(filteredOptions[activeIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  };

  /** Trigger load-more when the user scrolls near the bottom of the dropdown. */
  const handleDropdownScroll = React.useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      if (!hasMore || !onLoadMore || isLoading) return;
      const el = e.currentTarget;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < SCROLL_LOAD_THRESHOLD_PX) {
        onLoadMore();
      }
    },
    [hasMore, onLoadMore, isLoading],
  );

  const prevValueRef = React.useRef(value);
  /**
   * Sync the input label only from external value changes (never re-force while typing).
   * Options may arrive after `value` (edit load / enrichment): fill the label once if
   * the input still shows the raw id as a fallback.
   */
  React.useEffect(() => {
    if (value === prevValueRef.current) {
      if (!isEditingRef.current && value && selectedOption) {
        if (!searchQuery || searchQuery === value) {
          setSearchQuery(selectedOption.label);
        }
      }
      return;
    }

    prevValueRef.current = value;
    if (isEditingRef.current) return;

    if (!value) {
      setSearchQuery('');
    } else if (selectedOption) {
      setSearchQuery(selectedOption.label);
    } else {
      setSearchQuery('');
    }
  }, [value, selectedOption, searchQuery]);

  React.useEffect(() => {
    /** Close and restore the selected label when the click is outside. */
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setActiveIndex(-1);
        isEditingRef.current = false;
        if (value) {
          const selected = options.find((opt) => opt.value === value);
          if (selected) {
            setSearchQuery(selected.label);
          }
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, options]);

  const showMinLengthHint = searchQuery.length > 0 && searchQuery.length < minSearchLength;

  return (
    <div className={cn('w-full space-y-2', className)} ref={dropdownRef}>
      {label && (
        <Label htmlFor={inputId} className={cn('form-label', required && 'form-label-required')}>
          {label}
        </Label>
      )}

      <div className="relative">
        <Search
          className={cn(
            'modern-input-icon modern-input-icon-left',
            inputSize === 'sm' ? 'modern-input-icon-sm' : 'modern-input-icon-md',
          )}
        />
        <ShadcnInput
          ref={inputRef}
          id={inputId}
          type="text"
          autoComplete="off"
          value={searchQuery}
          onChange={handleInputChange}
          onBlur={() => {
            isEditingRef.current = false;
            // If selection is still set, put the full valid label back
            if (value) {
              const selected = options.find((opt) => opt.value === value);
              if (selected) {
                setSearchQuery(selected.label);
              }
            }
            onBlur?.();
          }}
          onFocus={() => {
            isEditingRef.current = true;
            // Keep the current label so the user can edit it (delete some/all chars)
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={cn(
            inputSize === 'sm' ? 'modern-input-sm' : 'modern-input-md',
            'modern-input-default',
            'modern-input-with-icon-left modern-input-with-icon-right',
            error && 'modern-input-error',
            state === 'error' && 'modern-input-error',
            state === 'success' && 'modern-input-success',
            state === 'warning' && 'modern-input-warning',
          )}
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center pointer-events-none">
          {isLoading && (
            <Loader2 className="h-4 w-4 animate-spin pointer-events-none search-spinner" />
          )}
          {!isLoading && value && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="h-4 w-4 flex items-center justify-center pointer-events-auto"
            >
              <X className="h-4 w-4 text-[var(--color-2-nuance-2)]" />
            </button>
          )}
        </div>

        {showMinLengthHint && (
          <div className="absolute z-50 mt-2 w-full rounded-xl border border-border/50 bg-popover/95 backdrop-blur-lg shadow-md p-3 text-center text-sm text-muted-foreground animate-in fade-in-0 slide-in-from-top-2 duration-200">
            <p className="font-medium">
              Tapez au moins {minSearchLength} caractères pour rechercher
            </p>
          </div>
        )}

        {isOpen && !showMinLengthHint && filteredOptions.length > 0 && (
          <div
            ref={optionsListRef}
            className="modern-dropdown modern-dropdown-lg"
            onScroll={handleDropdownScroll}
          >
            {filteredOptions.map((option, index) => (
              <button
                key={option.value}
                type="button"
                disabled={option.disabled}
                className={cn(
                  'modern-dropdown-item',
                  'stagger-item',
                  value === option.value && 'modern-dropdown-item-selected',
                  activeIndex === index && 'modern-dropdown-item-keyboard-active',
                )}
                style={{ '--stagger-index': index } as React.CSSProperties}
                onClick={() => handleSelect(option.value)}
                onMouseEnter={() => setActiveIndex(index)}
              >
                {highlightMatch(option.label, searchQuery, minSearchLength)}
              </button>
            ))}
            {isLoading && filteredOptions.length > 0 && (
              <div className="flex items-center justify-center py-2 px-3">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        )}

        {isOpen &&
          !showMinLengthHint &&
          filteredOptions.length === 0 &&
          searchQuery.length >= minSearchLength &&
          !isLoading && (
            <div className="modern-dropdown">
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Search className="h-12 w-12 text-muted-foreground/30 mb-3" />
                <p className="text-base font-medium text-muted-foreground">No results found</p>
                <p className="text-sm text-muted-foreground/70 mt-1">Try different keywords</p>
              </div>
            </div>
          )}

        {isOpen && isLoading && searchQuery.length >= minSearchLength && (
          <div className="modern-dropdown">
            {SKELETON_WIDTHS.map((width, i) => (
              <div
                key={width}
                className="modern-dropdown-item"
                style={{ '--stagger-index': i } as React.CSSProperties}
              >
                <div
                  className="h-3.5 rounded animate-pulse"
                  style={{
                    width,
                    background: 'color-mix(in srgb, var(--color-2-nuance-1) 18%, transparent)',
                  }}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {error && <p className="form-error-text">{error}</p>}
      {helperText && !error && <p className="form-helper-text">{helperText}</p>}
    </div>
  );
};
